import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, FontAwesomeIcon } from '@appcharge/shared-ui';

import Checkbox from 'components/Checkbox/Checkbox';
import { CheckbokItem } from 'components/FilterComponents/CheckList/CheckbokItem';
import { Popover } from 'components/Popover';

export interface CurrencySelectItem {
  label: string;
  value: string;
  renderFunction?: () => React.ReactNode;
}

export interface CurrencySelectProps {
  items: CurrencySelectItem[];
  selectedItems: string[];
  onChange: (values: string[]) => void;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  renderValue?: (selected: string[]) => string;
  fullWidth?: boolean;
  truncateText?: boolean;
}

const CurrencySelect = ({
  items,
  selectedItems,
  onChange,
  label,
  placeholder = 'Select currencies',
  error = false,
  helperText = '',
  renderValue,
  fullWidth = false,
  truncateText = false
}: CurrencySelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  const displayText = useMemo(() => {
    if (!selectedItems || selectedItems.length === 0) {
      return placeholder;
    }
    if (renderValue) {
      return renderValue(selectedItems);
    }
    return `${selectedItems.length} selected`;
  }, [selectedItems, placeholder, renderValue]);

  const allSelected = useMemo(() => {
    return (
      items.length > 0 &&
      items.every((item) => selectedItems.includes(item.value))
    );
  }, [items, selectedItems]);

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      const newSelection = selectedItems.filter(
        (id) => !items.some((item) => item.value === id)
      );
      onChange(newSelection);
    } else {
      const valuesToAdd = items
        .map((item) => item.value)
        .filter((val) => !selectedItems.includes(val));
      const newSelection = [...selectedItems, ...valuesToAdd];
      onChange(newSelection);
    }
  }, [allSelected, items, selectedItems, onChange]);

  const handleToggle = useCallback(
    (value: string) => {
      const newSelection = selectedItems.includes(value)
        ? selectedItems.filter((v) => v !== value)
        : [...selectedItems, value];
      onChange(newSelection);
    },
    [selectedItems, onChange]
  );

  useEffect(() => {
    if (!isOpen) return;

    const listener = (event: MouseEvent) => {
      if (selectRef.current?.contains(event.target as Node)) {
        return;
      }
      setIsOpen(false);
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('click', listener);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', listener);
    };
  }, [isOpen]);

  const handleToggleDropdown = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const trigger = (
    <Button
      type="button"
      variant="outline"
      onClick={handleToggleDropdown}
      className="w-full justify-between"
    >
      <span
        className={`font-normal ${selectedItems.length === 0 ? 'text-muted-foreground' : ''} ${truncateText ? 'truncate overflow-hidden text-ellipsis max-w-[calc(100%-24px)]' : ''}`}
      >
        {displayText}
      </span>
      <FontAwesomeIcon
        icon="fa-solid fa-chevron-down"
        className={`text-base-foreground transition-transform duration-200 flex-shrink-0 text-[#404040] ${isOpen ? 'rotate-180' : ''}`}
      />
    </Button>
  );

  return (
    <div ref={selectRef} className="relative w-full">
      {label && (
        <label className="block text-p-s font-medium text-base-foreground mb-2">
          {label}
        </label>
      )}

      <Popover
        width="100%"
        maxWidth="100%"
        height="auto"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        trigger={trigger}
        fullWidth={fullWidth}
      >
        <div className="flex flex-col rounded-md bg-base-popover text-xs pb-1">
          <div className="pl-3 py-2 border-b border-base-input">
            <Checkbox
              id="select-all"
              className="[&[data-state=checked]]:bg-indigo-500 [&[data-state=indeterminate]]:bg-indigo-500"
              label="Select all"
              checked={allSelected}
              onCheckedChange={handleSelectAll}
            />
          </div>
          <div className="flex flex-col gap-2 max-h-[200px] overflow-y-auto ml-2 mt-3">
            {items.length > 0 ? (
              items.map((item) => (
                <CheckbokItem
                  key={item.value}
                  item={item}
                  selectedItems={selectedItems}
                  onChange={handleToggle}
                />
              ))
            ) : (
              <div className="text-center text-xs text-base-foreground/50">
                No results found
              </div>
            )}
          </div>
        </div>
      </Popover>

      {helperText && (
        <div
          className={`text-p-s mt-2 ${error ? 'text-base-destructive' : 'text-base-mutedForeground'}`}
        >
          {helperText}
        </div>
      )}
    </div>
  );
};

export default CurrencySelect;
